/**
 * @module CanvasReasoningPanel
 * @purpose Right-column reasoning panel shell — header, stale banner, body state switch, virtualised cluster list.
 * @depends useReasoningRun, useReasoningClusters, useForceRecompute, ForceRecomputeDialog, react-window
 * @dependedOnBy CanvasView
 *
 * @rules
 * - Performance: react-window v2 List with useDynamicRowHeight keeps ≤30 cards in DOM
 * - State: local only (via hooks); no shared context
 * - Errors: error string from useReasoningRun displayed inline with retry button
 */

import { useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { List, useDynamicRowHeight } from 'react-window'
import { useReasoningRun, useReasoningClusters } from '../../../../hooks/use-reasoning'
import { useForceRecompute } from '../../../../hooks/use-force-recompute'
import { PanelHeader } from './CanvasReasoningPanelHeader'
import { ForceRecomputeDialog } from './ForceRecomputeDialog'
import {
  COMPLETED_OUTCOME_BODY_STATES,
  computeLaunchLocked,
  isFailedOrSupersededStatus,
  resolveBodyState,
  resolveDialogVariant,
} from './CanvasReasoningPanelState'
import type { BodyState } from './CanvasReasoningPanelState'
import { ReasoningLoadingState } from './ReasoningLoadingState'
import { ReasoningRunningState } from './ReasoningRunningState'
import { ReasoningNotStartedState } from './ReasoningNotStartedState'
import { ReasoningEmptyState } from './ReasoningEmptyState'
import { ReasoningErrorState } from './ReasoningErrorState'
import { ReasoningStaleBanner } from './ReasoningStaleBanner'
import type { ReasoningStaleInfo } from './ReasoningStaleBanner'
import { ReasoningFailedBanner } from './ReasoningFailedBanner'
import type { ReasoningFailedBannerErrorMessage } from './ReasoningFailedBanner'
import { ReasoningClusterCard } from './ReasoningClusterCard'
import type {
  ResolvedObservation,
  ClusterDetail,
  ClusterSummary,
  ReasoningRun,
  ReasoningRunStatus,
} from '../../../../types/reasoning'

const PANEL_WIDTH = '320px'
const EXPANDED_BY_DEFAULT = 5
const LIST_HEIGHT_PX = 500

interface CanvasReasoningPanelProps {
  caseId: string
  onClose: () => void
  /**
   * Supplied by `CanvasView` from the single canonical `useCanvasState`.
   *
   * This panel previously called `useCanvasState({ caseId })` itself purely to reach
   * `drillToCluster`, which instantiated a complete second canvas — a second `listArtefacts`, a
   * second facets query and a second copy of every filter. Worse, the filters that
   * `drillToCluster` set belonged to that second instance, so "Open in cluster canvas" mutated an
   * orphan state object nothing rendered, which is why the feature did not work.
   */
  drillToCluster: (cluster: ClusterDetail, resolved: Map<string, ResolvedObservation>) => void
}

interface ListRowSharedProps {
  caseId: string
  runId: string
  clusters: ClusterSummary[]
  expandedSet: Set<string>
  onToggleExpanded: (id: string) => void
  onDrillToCluster: (cluster: ClusterSummary, resolved: Map<string, ResolvedObservation>) => void
}

function ClusterRow({
  ariaAttributes,
  index,
  style,
  caseId,
  runId,
  clusters,
  expandedSet,
  onToggleExpanded,
  onDrillToCluster,
}: {
  ariaAttributes: Record<string, unknown>
  index: number
  style: React.CSSProperties
} & ListRowSharedProps): React.ReactElement | null {
  const cluster = clusters[index]
  if (cluster === undefined) return null
  const id = cluster.clusterId
  return (
    <div style={style} {...ariaAttributes}>
      <ReasoningClusterCard
        caseId={caseId}
        runId={runId}
        cluster={cluster}
        expanded={expandedSet.has(id)}
        onToggleExpanded={() => { onToggleExpanded(id) }}
        onDrillToCluster={(resolved) => { onDrillToCluster(cluster, resolved) }}
      />
    </div>
  )
}

interface PanelBodyProps {
  bodyState: BodyState
  caseId: string
  runId: string | null
  /** null when the run is not stale — the banner's render gate and its data in one value. */
  stale: ReasoningStaleInfo | null
  onRetry: () => void
  onRequestRecompute: () => void
  drillToCluster: (cluster: ClusterDetail, resolved: Map<string, ResolvedObservation>) => void
  /** The failed-run banner element for 'emptyWithBanner'/'listWithBanner', else null.
   *  Built once by the caller so both branches render the SAME element. */
  failedBanner: React.ReactElement | null
}

function PanelBody({
  bodyState,
  caseId,
  runId,
  stale,
  onRetry,
  onRequestRecompute,
  drillToCluster,
  failedBanner,
}: PanelBodyProps): React.ReactElement {
  if (bodyState === 'loading') return <ReasoningLoadingState />
  if (bodyState === 'running') return <ReasoningRunningState />
  if (bodyState === 'error') return <ReasoningErrorState onRetry={onRetry} />
  if (bodyState === 'failed') return <ReasoningErrorState onRetry={onRequestRecompute} />
  // request() opens the confirmation dialog -- every launch is confirmed, so
  // no body-state CTA ever calls launch() directly.
  if (bodyState === 'notStarted') return <ReasoningNotStartedState onLaunch={onRequestRecompute} />
  if (bodyState === 'empty') return <ReasoningEmptyState onRunReasoning={onRequestRecompute} />
  // emptyWithBanner: a failed/superseded latest run whose completed run
  // surfaced zero clusters -- the banner is the PERSISTENT failed-run state,
  // rendered above the empty-state copy, never in place of it.
  if (bodyState === 'emptyWithBanner') {
    return (
      <div>
        {failedBanner}
        <ReasoningEmptyState onRunReasoning={onRequestRecompute} />
      </div>
    )
  }
  if (runId === null) return <ReasoningEmptyState onRunReasoning={onRequestRecompute} />
  return (
    <ClusterList
      caseId={caseId}
      runId={runId}
      stale={stale}
      onRequestRecompute={onRequestRecompute}
      drillToCluster={drillToCluster}
      failedBanner={bodyState === 'listWithBanner' ? failedBanner : null}
    />
  )
}

interface ClusterListProps {
  caseId: string
  runId: string
  stale: ReasoningStaleInfo | null
  onRequestRecompute: () => void
  drillToCluster: (cluster: ClusterDetail, resolved: Map<string, ResolvedObservation>) => void
  /** Rendered as the FIRST child, above ReasoningStaleBanner -- the failed-run
   *  banner is the persistent state; staleness is a property of the run it sits on. */
  failedBanner: React.ReactElement | null
}

function ClusterList({
  caseId,
  runId,
  stale,
  onRequestRecompute,
  drillToCluster,
  failedBanner,
}: ClusterListProps): React.ReactElement {
  const { t } = useTranslation('analysis')
  const { clusters, total, loadingMore, loadMore } = useReasoningClusters(caseId, runId)
  const dynamicRowHeight = useDynamicRowHeight({ defaultRowHeight: 80 })
  const [expandedSet, setExpandedSet] = useState<Set<string>>(new Set<string>())
  const initialised = useRef(false)

  if (!initialised.current && clusters.length > 0) {
    initialised.current = true
    setExpandedSet(new Set<string>(
      clusters.slice(0, EXPANDED_BY_DEFAULT).map((cl) => cl.clusterId),
    ))
  }

  const handleToggleExpanded = useCallback((id: string) => {
    setExpandedSet((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }, [])

  const handleDrillToCluster = useCallback(
    (cl: ClusterSummary, resolved: Map<string, ResolvedObservation>) => {
      drillToCluster(cl as unknown as ClusterDetail, resolved)
    },
    [drillToCluster],
  )

  const rowProps: ListRowSharedProps = {
    caseId,
    runId,
    clusters,
    expandedSet,
    onToggleExpanded: handleToggleExpanded,
    onDrillToCluster: handleDrillToCluster,
  }

  return (
    <div>
      {failedBanner}
      {stale !== null && <ReasoningStaleBanner stale={stale} onRerun={onRequestRecompute} />}
      <List<ListRowSharedProps>
        rowCount={clusters.length}
        rowHeight={dynamicRowHeight}
        rowComponent={ClusterRow}
        rowProps={rowProps}
        style={{ height: `${String(LIST_HEIGHT_PX)}px` }}
      />
      {clusters.length < total && (
        <button
          type="button"
          disabled={loadingMore}
          onClick={() => { void loadMore() }}
          className="w-full py-2 text-xs uppercase tracking-wide"
          style={{ fontFamily: 'var(--font-mono)', color: 'var(--accent)', borderTop: '1px solid var(--border)' }}
        >
          {t('reasoning.panel.header.clusterCount', { count: total - clusters.length })}
        </button>
      )}
    </div>
  )
}

/**
 * Derived, null-safe view of the current run for the panel chrome.
 *
 * `stale` is null when the run is not stale, and otherwise carries everything the
 * banner needs. The counts are passed through UNCHANGED — in particular null is
 * NOT coerced to 0, because null means "not measured" and 0 means "measured,
 * nothing excluded". Collapsing them here would tell the investigator that a run
 * nobody checked came back clean.
 */
function describeRun(run: ReasoningRun | null): {
  runId: string | null
  stale: ReasoningStaleInfo | null
  clusterCount: number | null
  unclusteredRowCount: number | null | undefined
  completedAt: string | null
} {
  return {
    runId: run?.reasoningRunId ?? null,
    stale: deriveStale(run),
    clusterCount: run?.clusterCount ?? null,
    unclusteredRowCount: run?.unclusteredInvestigativeRowCount,
    completedAt: run?.completedAt ?? null,
  }
}

/**
 * The banner's data, or null when the run is not stale.
 *
 * Counts pass through UNCHANGED — null is NOT coerced to 0. null means "not
 * measured"; 0 means "measured, nothing excluded". Collapsing them here would tell
 * the investigator that a run nobody checked came back clean.
 */
function deriveStale(run: ReasoningRun | null): ReasoningStaleInfo | null {
  if (run?.frontierStale !== true) return null
  return {
    staleReason: run.staleReason ?? null,
    excludedEvidenceFileCount: run.excludedEvidenceFileCount ?? null,
    excludedArtefactRowCount: run.excludedArtefactRowCount ?? null,
  }
}

interface FailedBannerInfo {
  failedAt: string | null
  errorMessage: ReasoningFailedBannerErrorMessage | null
}

/**
 * The failed-run banner's data, derived from `run` -- NEVER from `completedRun`,
 * which may be a wholly different, earlier run. `failedAt` reads `run.completedAt`
 * because the startup sweep stamps `completed_at` when it marks an interrupted run
 * `failed`, so this is the failure timestamp, not a success timestamp.
 */
function deriveFailedBannerInfo(run: ReasoningRun | null): FailedBannerInfo {
  if (run === null || !isFailedOrSupersededStatus(run.status)) {
    return { failedAt: null, errorMessage: null }
  }
  return { failedAt: run.completedAt, errorMessage: run.errorMessage }
}

/**
 * The failed-run banner, or null outside 'emptyWithBanner'/'listWithBanner'.
 * `onRetry` is always the panel's `request()` -- the retry DIALOG, never a
 * direct launch.
 */
function buildFailedBanner(
  run: ReasoningRun | null,
  bodyState: BodyState,
  onRetry: () => void,
): React.ReactElement | null {
  if (bodyState !== 'emptyWithBanner' && bodyState !== 'listWithBanner') return null
  const { failedAt, errorMessage } = deriveFailedBannerInfo(run)
  return <ReasoningFailedBanner onRetry={onRetry} failedAt={failedAt} errorMessage={errorMessage} />
}

interface HeaderView {
  runId: string | null
  stale: ReasoningStaleInfo | null
  clusterCount: number | null
  unclusteredRowCount: number | null | undefined
  completedAt: string | null
  runStatus: ReasoningRunStatus | undefined
}

/**
 * FR2/AC12/AC13: the header reads `completedRun` in every state whose body
 * renders the completed run's outcome, and `run` everywhere else. The label
 * source and the status prop move TOGETHER, so a completed-at label never
 * renders against a different run's timestamp than the one it describes.
 */
function resolveHeaderView(
  run: ReasoningRun | null,
  completedRun: ReasoningRun | null,
  bodyState: BodyState,
): HeaderView {
  if (bodyState === 'running') {
    // Duncan's defect 1 (2026-09-05): the launch request runs the whole
    // reasoning pass synchronously, so nothing refetches `run` until it
    // settles -- an in-place launch leaves the PREVIOUS completed run loaded
    // while the body already shows the running state. The header must not
    // describe that stale run: blank every outcome label for the duration.
    return { ...describeRun(null), runStatus: run?.status }
  }
  const isCompletedOutcome = COMPLETED_OUTCOME_BODY_STATES.has(bodyState)
  const source = isCompletedOutcome ? completedRun : run
  return {
    ...describeRun(source),
    runStatus: isCompletedOutcome ? completedRun?.status : run?.status,
  }
}

interface LaunchMessageStripsProps {
  errorKey: string | null
  errorValues: Record<string, number> | null
  infoKey: string | null
}

/**
 * FR6: the launch outcome, rendered above the body, never as raw backend text.
 *
 * @behaviour errorKey renders in a role="alert" strip, infoKey in a
 *   role="status" strip; either may be present alone. Both are cleared by the
 *   hook on the next launch attempt (`request`/`launch`). `errorValues` (when
 *   present) supplies i18next interpolation values for errorKey, e.g. the
 *   volume-guard's observation count and ceiling.
 */
function LaunchMessageStrips({
  errorKey,
  errorValues,
  infoKey,
}: LaunchMessageStripsProps): React.ReactElement | null {
  const { t } = useTranslation('analysis')
  if (errorKey === null && infoKey === null) return null
  return (
    <div className="flex flex-col gap-1 px-3 py-2">
      {errorKey !== null && (
        <div
          role="alert"
          className="rounded border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700 dark:border-red-900 dark:bg-red-950/50 dark:text-red-300"
        >
          {errorValues !== null ? t(errorKey as never, errorValues) : t(errorKey as never)}
        </div>
      )}
      {infoKey !== null && (
        <div
          role="status"
          className="rounded border px-2 py-1 text-xs"
          style={{ borderColor: 'var(--border)', color: 'var(--text-muted)', background: 'var(--accent-dim)' }}
        >
          {t(infoKey as never)}
        </div>
      )}
    </div>
  )
}

/**
 * Reasoning panel shell with header, optional stale banner, and body state switch.
 *
 * @behaviour Renders loading/error/notStarted/empty/list states based on
 *            useReasoningRun output. List state uses react-window v2 List with
 *            useDynamicRowHeight for DOM-capped virtualisation. Every launch
 *            entry point funnels through request() -> confirm() -> launch(),
 *            so a launch is always confirmed and the header trigger locks
 *            until a logical end (FR2).
 * @errors Inline error banner with retry callback; launch errors surface via
 *            the errorKey message strip, never raw backend text.
 */
export function CanvasReasoningPanel({ caseId, onClose, drillToCluster }: CanvasReasoningPanelProps): React.ReactElement {
  const { run, completedRun, loading, error, refetch } = useReasoningRun(caseId, true)
  const {
    isConfirming,
    isComputing,
    errorKey,
    errorValues,
    infoKey,
    request: requestRecompute,
    confirm: confirmRecompute,
    cancel: cancelRecompute,
  } = useForceRecompute(caseId, { onSettled: () => { void refetch() } })

  const runExists = run !== null
  const launchLocked = computeLaunchLocked({
    isComputing,
    loading,
    error,
    runStatus: run?.status,
    runExists,
  })

  const bodyState = resolveBodyState({
    isComputing,
    loading,
    error,
    clusterCount: run?.clusterCount,
    runStatus: run?.status,
    runExists,
    completedRun,
  })

  const { runId, stale, clusterCount, unclusteredRowCount, completedAt, runStatus } =
    resolveHeaderView(run, completedRun, bodyState)
  const failedBanner = buildFailedBanner(run, bodyState, requestRecompute)

  const dialogVariant = resolveDialogVariant(run?.status)
  const dialogForce = dialogVariant !== 'firstRun'

  return (
    <div
      className="flex h-full flex-col overflow-hidden"
      style={{ width: PANEL_WIDTH, borderLeft: '1px solid var(--border)' }}
    >
      <PanelHeader
        clusterCount={clusterCount}
        unclusteredRowCount={unclusteredRowCount}
        completedAt={completedAt}
        runStatus={runStatus}
        launchLocked={launchLocked}
        onForceRecompute={requestRecompute}
        onClose={onClose}
      />

      <LaunchMessageStrips errorKey={errorKey} errorValues={errorValues} infoKey={infoKey} />

      <div className="flex-1 overflow-y-auto">
        <PanelBody
          bodyState={bodyState}
          caseId={caseId}
          runId={runId}
          stale={stale}
          onRetry={() => { void refetch() }}
          onRequestRecompute={requestRecompute}
          drillToCluster={drillToCluster}
          failedBanner={failedBanner}
        />
      </div>

      <ForceRecomputeDialog
        variant={dialogVariant}
        open={isConfirming}
        onConfirm={() => { void confirmRecompute(dialogForce) }}
        onCancel={cancelRecompute}
      />
    </div>
  )
}
